import { useEffect, useMemo, useState } from "react";
import {
  LayoutDashboard,
  ClipboardList,
  Users,
  Smartphone,
  Package,
  DollarSign,
  Settings,
  Search,
  Bell,
  ChevronDown,
  TruckIcon,
  LogOut,
  Tag,
  StickyNote,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { HEADER_BG } from "../lib/constants";
import Dashboard from "../views/Dashboard";
import OrdersView from "../views/OrdersView";
import SaidaView from "../views/SaidaView";
import FechamentoView from "../views/FechamentoView";
import ClientesView from "../views/ClientesView";
import VendaPecasView from "../views/VendaPecasView";
import NotesView from "../views/NotesView";
import FinanceiroView from "../views/FinanceiroView";
import PriceListView from "../views/PriceListView";
import SettingsView from "../views/SettingsView";
import NewOrderModal from "../views/NewOrderModal";
import ExitModal from "../views/ExitModal";
import OrderDetailModal from "../views/OrderDetailModal";
import api from "../lib/api";

const NAV = [
  { icon: LayoutDashboard, label: "Painel", view: "dashboard" },
  { icon: ClipboardList, label: "Entrada (OS)", view: "orders" },
  { icon: TruckIcon, label: "Saída", view: "saida" },
  { icon: Package, label: "Fechamento", view: "fechamento" },
  { icon: Users, label: "Clientes", view: "clientes" },
  { icon: Smartphone, label: "Venda de peças", view: "vendapecas" },
  { icon: StickyNote, label: "Anotações", view: "notes" },
  { icon: DollarSign, label: "Financeiro", view: "financeiro", requires: "financeiro" },
  { icon: Tag, label: "Tabela de preços", view: "prices", requires: "admin" },
];

export default function Main() {
  const { user, logout } = useAuth();
  const role = user?.role || "admin";
  const isAdmin = role === "admin";
  const canSeeFinanceiro = role === "admin" || role === "manager";
  const [view, setView] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [pieceSales, setPieceSales] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [exitOrder, setExitOrder] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);

  // Global search
  const [searchQuery, setSearchQuery] = useState("");
  const [showUserMenu, setShowUserMenu] = useState(false);

  const goToView = (target) => {
    if (target === "financeiro" && !canSeeFinanceiro) return;
    if (target === "settings" && !isAdmin) return;
    if (target === "prices" && !isAdmin) return;
    setView(target);
  };

  const load = async () => {
    try {
      const [o, c, p] = await Promise.all([
        api.get("/orders"),
        api.get("/clients"),
        api.get("/piece-sales"),
      ]);
      setOrders(o.data);
      setClients(c.data);
      setPieceSales(p.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const stats = useMemo(
    () => ({
      received: orders.filter((o) => o.status === "received").length,
      ready: orders.filter((o) => o.status === "ready").length,
      delivered: orders.filter((o) => o.status === "delivered").length,
      revenue: orders
        .filter((o) => o.status === "delivered")
        .reduce((s, o) => s + (Number(o.total) || 0), 0),
    }),
    [orders]
  );

  // ─── Actions ──────────────────────────────────────────────────────────────
  const refreshOrders = async () => {
    const r = await api.get("/orders");
    setOrders(r.data);
  };
  const refreshClients = async () => {
    const r = await api.get("/clients");
    setClients(r.data);
  };
  const refreshPieceSales = async () => {
    const r = await api.get("/piece-sales");
    setPieceSales(r.data);
  };

  const updateOrder = async (id, patch) => {
    const { data } = await api.patch(`/orders/${id}`, patch);
    setOrders((prev) => prev.map((o) => (o.id === id ? data : o)));
    return data;
  };

  const revertOrderStatus = async (id) => {
    const order = orders.find((o) => o.id === id);
    if (!order) return;
    const prevMap = { ready: "received", delivered: "ready" };
    const target = prevMap[order.status];
    if (!target) return;
    const patch = { status: target };
    if (order.status === "delivered") {
      patch.exitDate = "";
      patch.exitTime = "";
      patch.payMethod = null;
      patch.cartaoDesc = "";
    }
    await updateOrder(id, patch);
  };

  const completeExit = async (id, payMethod, cartaoDesc, costValue) => {
    const { data } = await api.post(`/orders/${id}/deliver`, { payMethod, cartaoDesc, costValue });
    setOrders((prev) => prev.map((o) => (o.id === id ? data : o)));
    setExitOrder(null);
  };

  const createOrder = async (payload) => {
    const { data } = await api.post("/orders", payload);
    setOrders((prev) => [data, ...prev]);
    setShowNewOrder(false);
    setView("orders");
  };

  const deleteOrder = async (id) => {
    await api.delete(`/orders/${id}`);
    setOrders((prev) => prev.filter((o) => o.id !== id));
  };

  const currentMonth = new Date().toLocaleDateString("pt-BR", { month: "long" }).toUpperCase();
  const isOrdersActive = ["orders"].includes(view);

  return (
    <div className="min-h-screen flex bg-[var(--app-bg)]">
      {/* Sidebar */}
      <aside
        className="hidden md:flex md:w-60 flex-shrink-0 bg-white border-r border-slate-200 flex-col"
        data-testid="sidebar"
      >
        <div className="h-20 px-4 flex items-center justify-center border-b border-slate-200 bg-white">
          <img
            src="/logo-sm.png"
            alt="Phone Lab — Assistência Técnica"
            className="max-h-12 w-auto object-contain"
            data-testid="sidebar-logo"
          />
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {NAV.filter((item) => {
            if (item.requires === "financeiro") return canSeeFinanceiro;
            if (item.requires === "admin") return isAdmin;
            return true;
          }).map((item) => {
            const active = view === item.view || (item.view === "orders" && isOrdersActive);
            const Icon = item.icon;
            return (
              <button
                key={item.label}
                onClick={() => goToView(item.view)}
                data-testid={`nav-${item.view}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  active
                    ? "bg-blue-600 text-white shadow-sm"
                    : "text-slate-600 hover:bg-blue-50 hover:text-blue-700"
                }`}
              >
                <Icon className="w-[18px] h-[18px]" />
                {item.label}
              </button>
            );
          })}
          {isAdmin && (
            <button
              onClick={() => goToView("settings")}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                view === "settings"
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-slate-500 hover:bg-slate-100"
              }`}
              data-testid="nav-settings"
            >
              <Settings className="w-[18px] h-[18px]" />
              Configurações
            </button>
          )}
        </nav>

        <div className="p-3 border-t border-slate-200">
          <button
            onClick={logout}
            data-testid="logout-button"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-rose-50 hover:text-rose-700 transition-all"
          >
            <LogOut className="w-[18px] h-[18px]" />
            Sair
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header
          className="h-16 flex items-center px-6 gap-4 text-white shadow-sm"
          style={{ background: HEADER_BG }}
        >
          <h1 className="font-heading text-lg font-bold tracking-tight">Sistema de Gestão</h1>
          <span className="text-white/50">·</span>
          <span className="text-white/80 text-sm hidden md:inline">
            {NAV.find((n) => n.view === view)?.label || "Painel"}
          </span>

          <div className="flex-1 max-w-md ml-auto relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
            <input
              data-testid="global-search"
              type="text"
              placeholder="Buscar OS, cliente, modelo, IMEI..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-9 pl-9 pr-3 rounded-lg bg-white/10 border border-white/15 text-white placeholder:text-white/50 text-sm focus:bg-white/15 focus:border-white/25 focus:outline-none"
            />
          </div>

          <button className="p-2 rounded-lg hover:bg-white/10 transition-colors" data-testid="notifications-button">
            <Bell className="w-4 h-4" />
          </button>

          <div className="relative" data-testid="admin-menu">
            <button
              onClick={() => setShowUserMenu((v) => !v)}
              className="flex items-center gap-2 pl-3 border-l border-white/15 h-full pr-1 hover:bg-white/5 rounded transition-colors"
              data-testid="admin-menu-button"
            >
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-xs font-bold uppercase">
                {(user?.name || user?.username || "A").charAt(0)}
              </div>
              <div className="hidden md:block text-left">
                <div className="text-xs font-semibold leading-tight">{user?.name || "Admin"}</div>
                <div className="text-[10px] text-white/60 leading-tight">
                  {role === "admin" && "Administrador"}
                  {role === "manager" && "Gerente"}
                  {role === "employee" && "Funcionário"}
                </div>
              </div>
              <ChevronDown className={`w-3 h-3 text-white/60 transition-transform ${showUserMenu ? "rotate-180" : ""}`} />
            </button>

            {showUserMenu && (
              <>
                <div
                  className="fixed inset-0 z-30"
                  onClick={() => setShowUserMenu(false)}
                />
                <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl border border-slate-200 shadow-xl z-40 overflow-hidden animate-slide-down">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <div className="text-xs text-slate-500">Logado como</div>
                    <div className="text-sm font-semibold text-slate-900 truncate">{user?.name || "Admin"}</div>
                    <div className="text-[11px] text-slate-400 truncate">{user?.username}</div>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => { setShowUserMenu(false); goToView("settings"); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      data-testid="admin-menu-settings"
                    >
                      <Settings className="w-4 h-4 text-slate-400" />
                      Configurações
                    </button>
                  )}
                  <div className="border-t border-slate-100">
                    <button
                      onClick={logout}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition-colors"
                      data-testid="admin-menu-logout"
                    >
                      <LogOut className="w-4 h-4" />
                      Sair da conta
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-auto animate-fade-in">
          {loading ? (
            <div className="flex items-center justify-center h-full text-slate-400 text-sm">
              Carregando...
            </div>
          ) : (
            <>
              {view === "dashboard" && (
                <Dashboard
                  stats={stats}
                  orders={orders}
                  onNewOrder={() => setShowNewOrder(true)}
                  currentMonth={currentMonth}
                />
              )}
              {view === "orders" && (
                <OrdersView
                  orders={orders}
                  searchQuery={searchQuery}
                  currentMonth={currentMonth}
                  onNewOrder={() => setShowNewOrder(true)}
                  onExit={(o) => setExitOrder(o)}
                  onOpenDetail={(o) => setDetailOrder(o)}
                  onUpdateOrder={updateOrder}
                  onRevertStatus={revertOrderStatus}
                  onDeleteOrder={deleteOrder}
                />
              )}
              {view === "saida" && (
                <SaidaView
                  orders={orders}
                  searchQuery={searchQuery}
                  onExit={(o) => setExitOrder(o)}
                  onUpdateOrder={updateOrder}
                />
              )}
              {view === "fechamento" && (
                <FechamentoView
                  orders={orders}
                  pieceSales={pieceSales}
                  clients={clients}
                  searchQuery={searchQuery}
                  onRefresh={() => Promise.all([refreshOrders(), refreshPieceSales()])}
                />
              )}
              {view === "clientes" && (
                <ClientesView
                  clients={clients}
                  orders={orders}
                  onRefresh={refreshClients}
                  onOpenOrder={(o) => setDetailOrder(o)}
                />
              )}
              {view === "vendapecas" && (
                <VendaPecasView
                  pieceSales={pieceSales}
                  searchQuery={searchQuery}
                  clients={clients}
                  onRefresh={refreshPieceSales}
                />
              )}
              {view === "financeiro" && <FinanceiroView orders={orders} pieceSales={pieceSales} />}
              {view === "prices" && <PriceListView />}
              {view === "notes" && <NotesView />}
              {view === "settings" && <SettingsView />}
            </>
          )}
        </main>
      </div>

      {/* Modals */}
      {showNewOrder && (
        <NewOrderModal
          clients={clients}
          nextNumber={`OS-${String(orders.length + 1).padStart(3, "0")}`}
          onClose={() => setShowNewOrder(false)}
          onSave={createOrder}
        />
      )}
      {exitOrder && (
        <ExitModal order={exitOrder} onClose={() => setExitOrder(null)} onComplete={completeExit} />
      )}
      {detailOrder && (
        <OrderDetailModal
          order={orders.find((o) => o.id === detailOrder.id) || detailOrder}
          onClose={() => setDetailOrder(null)}
          onExit={() => {
            setDetailOrder(null);
            setExitOrder(detailOrder);
          }}
          onUpdate={updateOrder}
          onRefresh={load}
        />
      )}
    </div>
  );
}
